import { useCreateInvitation } from '../../hooks/useCircles'
import { useToast } from '../../contexts/ToastContext'
import { cn } from '../../lib/cn'
import { useRef, useState } from 'react'
import { usePerson } from '../../contexts/PersonContext'
import { tryCopyText } from '../../lib/clipboard'
import { useActionBoundary } from '../../hooks/useActionBoundary'

interface InviteFamilyButtonProps {
  circleId: string
  children?: React.ReactNode
  className?: string
}

/**
 * Mints a circle invite link and copies it to the clipboard (COTC_Circles R3). The design's
 * "generate a link" behaviour made real: any active member (owner or not) may invite.
 */
export function InviteFamilyButton({ circleId, children = 'Invite a family', className }: InviteFamilyButtonProps) {
  const { activePerson } = usePerson()
  return <CircleInvitation key={`${circleId}:${activePerson?.id ?? ''}:${sessionStorage.getItem('cot_jwt_token') ?? ''}`} circleId={circleId} className={className}>{children}</CircleInvitation>
}

function CircleInvitation({ circleId, children, className }: InviteFamilyButtonProps) {
  const { flash } = useToast()
  const invite = useCreateInvitation(circleId)
  const { role } = usePerson()
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const begin = useActionBoundary(circleId)

  async function onClick() {
    if (role !== 'parent' || inFlight.current) return
    const current = begin()
    inFlight.current = true; setBusy(true); setError(null)
    try {
      const link = url ?? (await invite.mutateAsync()).url
      if (!current()) return
      setUrl(link)
      const copied = await tryCopyText(link)
      if (!current()) return
      if (copied) flash('Invite link copied — send it to a parent you trust.')
      else setError('Copy was blocked. Select and copy the invite link below.')
    } catch (err) { if (current()) setError(err instanceof Error ? err.message : 'Could not create an invite link. Please try again.') }
    finally { if (current()) { inFlight.current = false; setBusy(false) } }
  }
  if (role !== 'parent') return null

  return (
    <span className="inline-flex w-full min-w-0 flex-col gap-2">
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'inline-flex items-center justify-center rounded-btn bg-ink px-[18px] py-3 text-[13.5px] font-medium text-paper-panel transition-colors hover:bg-ink/90',
        className,
      )}
    >
      {busy ? 'Creating link…' : children}
    </button>
    {error && <span role="alert" className="text-[12px] text-honey-fg">{error}</span>}
    {url && error && <input aria-label="Invite link" readOnly value={url} onFocus={(event) => event.currentTarget.select()} className="w-full min-w-0 rounded-input border border-line p-2 text-[12px] text-ink" />}
    </span>
  )
}
